<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { setLanguage } from '../i18n';

const { locale } = useI18n();

// Liste der verfügbaren Sprachen mit ihren Namen
const languages = [
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'en', name: 'English', flag: '🇬🇧' }
];

// Aktuelle Sprache
const currentLanguage = computed(() => {
  return languages.find(lang => lang.code === locale.value) || languages[0];
});

// Dropdown-Status
const isOpen = ref(false);

// Dropdown öffnen/schließen
const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
};

// Sprache ändern
const changeLanguage = (langCode) => {
  setLanguage(langCode);
  isOpen.value = false;
};

// Schließen des Dropdowns bei Klick außerhalb
const closeDropdown = (event) => {
  if (!event.target.closest('.language-switcher')) {
    isOpen.value = false;
  }
};

// Event-Listener für Klicks außerhalb hinzufügen/entfernen
onMounted(() => {
  document.addEventListener('click', closeDropdown);
});

onUnmounted(() => {
  document.removeEventListener('click', closeDropdown);
});
</script>

<template>
  <div class="language-switcher" @click.stop>
    <button 
      class="language-toggle" 
      @click="toggleDropdown"
      :aria-expanded="isOpen"
      aria-label="Sprache wechseln"
    >
      <span class="flag">{{ currentLanguage.flag }}</span>
      <span class="lang-code">{{ currentLanguage.code.toUpperCase() }}</span>
      <svg class="dropdown-arrow" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>
    
    <div class="language-dropdown" v-if="isOpen">
      <button 
        v-for="lang in languages" 
        :key="lang.code"
        class="language-option" 
        :class="{ 'active': lang.code === locale }"
        @click="changeLanguage(lang.code)"
      >
        <span class="flag">{{ lang.flag }}</span>
        <span class="lang-name">{{ lang.name }}</span>
      </button>
    </div>
  </div>
</template>

<style scoped>
.language-switcher {
  position: relative;
  margin: 0 1rem;
}

.language-toggle {
  display: flex;
  align-items: center;
  background: none;
  border: 1px solid var(--accent-color);
  border-radius: var(--radius-sm);
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  font-size: 0.85rem;
  color: var(--text-color);
  transition: var(--transition-normal);
}

.language-toggle:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.flag {
  margin-right: 0.5rem;
  font-size: 1rem;
}

.lang-code {
  margin-right: 0.5rem;
  font-weight: 500;
}

.dropdown-arrow {
  transition: transform 0.3s ease;
}

.language-toggle[aria-expanded="true"] .dropdown-arrow {
  transform: rotate(180deg);
}

.language-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 10;
  margin-top: 0.5rem;
  background-color: var(--white);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-md);
  min-width: 160px;
  overflow: hidden;
}

.language-option {
  display: flex;
  align-items: center;
  width: 100%;
  text-align: left;
  padding: 0.75rem 1rem;
  border: none;
  background: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.language-option:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.language-option.active {
  background-color: rgba(0, 132, 61, 0.1);
  color: var(--primary-color);
  font-weight: 500;
}

.lang-name {
  font-size: 0.9rem;
}

@media (max-width: 768px) {
  .language-switcher {
    margin: 0.5rem 0;
  }
}
</style> 